import React, { useState, useEffect } from "react";
import {
  Box,
  Form,
  Button,
  ResponsiveContext,
  Text,
  Layer,
  Spinner,
  Heading,
} from "grommet";

import ProductsSection from "../../components/ProficiencyPageComponents/ProductsSection";
import { HeaderNavigation } from "../../components/Headers/GlobalHeader";
import { StickyFooter } from "../../components/Footers/GlobalFooter";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { addProduct } from "../../redux/slices/proficiencySlice";
import { useParams } from "react-router-dom";
import axios from "axios";
import API_URL from "../../config/config";
import { set } from "mongoose";
import { getUserName } from "../../services/dashboardService";
import HomeButtons from "../../components/Buttons/HomeButtons";

const AddProficiency = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const role = useSelector((state) => state.profile.role);
  const { user_id } = useParams();
  const [products, setProducts] = useState([]);
  const [editIndex, setEditIndex] = useState(null);
  const [productSkills, setProductSkills] = useState([]);
  const [expertFlag, setExpertFlag] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [showAlert, setShowAlert] = useState(false);
  const [subProductSkills, setSubProductSkills] = useState([]);
  const [userName, setUserName] = useState("");
  const [productsToDelete, setProductsToDelete] = useState([]);
  const [subProductsToDelete, setSubProductsToDelete] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showSkillCheckModal, setShowSkillCheckModal] = useState(false);

  // Fetch role skills, product skills, and sub-product skills
  const fetchSkillsData = async () => {
    try {
      const response = await axios.get(`${API_URL}/user/getRolesAndSkills`);
      setProductSkills(response.data.product_skills);
      setSubProductSkills(response.data.sub_product_skills);
    } catch (error) {
      console.error("Error fetching skills data:", error);
    }
  };
  useEffect(() => {
    fetchSkillsData();
  }, []); // This runs only once to fetch role and product skills

  const [formData, setFormData] = useState({
    currentRole: role,
    totalExperience: "",
    productCategory: "",
    productName: "",
    productLevel: "",
    providerName: "",
    certificateName: "",
    achievementDate: "",
    expiryDate: "",
    neverExpires: false,
    certificateId: "",
    certificateUrl: "",
  });

  const size = React.useContext(ResponsiveContext);

  const handleInputChange = (name, value) => {
    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: value,
    }));
  };

  const handleExpertSelection = (isExpert) => {
    setExpertFlag(isExpert);
  };

  // Adjust handleDeleteProduct to keep track and manage deletion accurately
  const handleDeleteProduct = (index) => {
    const productToDelete = products[index];

    // Find the sub-product based on the subProductName in subProductSkills
    const subProductSkill = subProductSkills.find(
      (subProduct) =>
        subProduct.skill_name === productToDelete.subProductName &&
        subProduct.product_name === productToDelete.productName,
    );

    if (subProductSkill) {
      setSubProductsToDelete([...subProductsToDelete, subProductSkill.id]);
    }

    setProducts(products.filter((_, i) => i !== index));
    setProductsToDelete([...productsToDelete, productToDelete]); // Add to deletion list
  };

  // When a new product is added, ensure productsToDelete is cleared if no products remain to be deleted
  useEffect(() => {
    if (products.length === 0) {
      setProductsToDelete([]); // Clear productsToDelete when no products are present
    }
  }, [products]);
  const handleSubmit = async () => {
    setLoading(true);

    if (products.length > 6) {
      setAlertMessage("Error! You can only add your top 6 skills.");
      setShowAlert(true);
      return;
    }

    if (products.length === 0) {
      setAlertMessage(
        "Please add at least one product before submitting the form.",
      );
      setShowAlert(true);
      return;
    }

    setAlertMessage("");
    setShowAlert(false);

    products.forEach((product) => dispatch(addProduct(product)));

    const productIds = products.map((product) => product.id);
    const subProductSkillIds = products
      .map((product) => product.subProductSkillId)
      .filter(Boolean);
    console.log("products", products);
    const productCount = products.length;
    const productDetails = products
      .filter((product) => product.subProductSkillId) // Filter out products without subProductSkillId
      .map((product) => ({
        subProductSkillId: product.subProductSkillId,
        productLevel: product.productLevel,
      }));
    const fullFormData = {
      user_id: user_id,
      // skills: skillIds,
      products: productIds,
      productDetails: productDetails,
      subProducts: subProductSkillIds,
    };

    try {
      // Once deletion is done (or if no items to delete), call the submit API
      await axios.post(`${API_URL}/user/addUserHistory`, { fullFormData });
      setLoading(false);
      // Navigate to quiz settings after successful submission
      navigate("/quiz-settings", {
        state: {
          user_id,
          expertFlag,
        },
      });
    } catch (error) {
      console.error("Error handling profile update:", error);
      // Handle the error appropriately (e.g., show an error message to the user)
    }
  };

  const handleProceedToSkillsCheck = () => {
    setShowSkillCheckModal(true); // Open the modal when the user clicks the button
  };

  const handleCloseModal = () => {
    setShowSkillCheckModal(false); // Close the modal when the user clicks "Cancel" or "Close"
  };

  const handleContinue = () => {
    setShowSkillCheckModal(false); // Close the modal and submit the form
    handleSubmit(); // Call the existing form submission logic
  };

  if (loading) {
    return (
      <Box fill align="center" justify="center">
        <Spinner size="large" />
        <Text>Processing...</Text>
      </Box>
    );
  }
  return (
    <Box fill="vertical" justify="between">
      <Box flex="grow">
        <HeaderNavigation />
        <HomeButtons user_id={user_id} flag={1} />
        <Box align="center" justify="center" pad="medium">
          <Form onSubmit={handleSubmit}>
            <Box pad="small" gap="medium">
              <ProductsSection
                size={size}
                formData={formData}
                setFormData={setFormData}
                handleInputChange={handleInputChange}
                products={products}
                setProducts={setProducts}
                editIndex={editIndex}
                setEditIndex={setEditIndex}
                productSkills={productSkills}
                subProductSkills={subProductSkills}
                setSubProductSkills={setSubProductSkills}
                handleExpertSelection={handleExpertSelection}
                handleDeleteProduct={handleDeleteProduct} // Add this prop
              />
            </Box>
            <Box margin={{ top: "medium" }} width="100%" align="center">
              <Button
                type="button"
                label="Proceed to skills check"
                primary
                onClick={handleProceedToSkillsCheck}
                disabled={products.length === 0}
              />
            </Box>
          </Form>
        </Box>
      </Box>
      <StickyFooter />

      {showSkillCheckModal && (
        <Layer onEsc={handleCloseModal} onClickOutside={handleCloseModal}>
          <Box pad="medium" gap="small" width="large">
            <Heading level={3} margin="none">
              Confirm Your Selected Skill(s)
            </Heading>
            <Text size="small">
              You have selected <strong>{products.length}</strong> skill(s) to
              proceed with.
            </Text>
            <Box>
              <Text size="small">
                <strong>Selected Skills:</strong>
              </Text>
              {products.map((product, index) => (
                <Text key={index} size="small">
                  {product.productName} - {product.subProductName} -{" "}
                  {product.productLevel === 1
                    ? "Beginner"
                    : product.productLevel === 2
                      ? "Intermediate"
                      : product.productLevel === 3
                        ? "Advanced"
                        : "Unknown"}
                </Text>
              ))}
            </Box>
            <Box direction="row" gap="medium" justify="end">
              <Button label="Cancel" onClick={handleCloseModal} />
              <Button label="Continue" primary onClick={handleContinue} />
            </Box>
          </Box>
        </Layer>
      )}

      {showAlert && (
        <Layer
          onEsc={() => setShowAlert(false)}
          onClickOutside={() => setShowAlert(false)}
        >
          <Box pad="medium" gap="small">
            <Text>{alertMessage}</Text>
            <Box direction="row" gap="medium" justify="center">
              <Button label="Cancel" onClick={() => setShowAlert(false)} />
            </Box>
          </Box>
        </Layer>
      )}
    </Box>
  );
};

export default AddProficiency;
